import React, { useContext, useEffect, useState, useRef } from 'react';
import EvidenceLightbox, { EvidenceGalleryContext, isImageEvidence, shouldOpenGallery } from '../../components/EvidenceLightbox';
import { evidenceStamp, uniqueEvidence, validPoint } from '../../lib/fieldOperation';
import { devicePendingMessage } from '../../lib/fieldOperation';
import EvidenceLayer from '../../components/EvidenceLayer';
import { applyReviewNA } from '../../lib/reviewIntegrity';
import {
  answered,
  displayStatus,
  errorMessage,
  evaluationPercentage,
  isFinding,
  missingFromError,
  percentageLabel,
  publicationWarning,
  visibilityMap,
} from '../../lib/evaluationIntegrity';
import { IntegrityFields, ScoreSummary, MissingList } from '../../components/EvaluationIntegrity';
import { getSectionStatsFromEval, sectionKey, sectionTitle, legacyCriterion } from '../../lib/evalSections';
import { itemIdentity } from '../../lib/checklistSave';
import { useParams, useNavigate } from 'react-router-dom';
import { evaluationsAPI, usersAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { getCriteriaBySection, SECTIONS, getSemaforoBarColor } from '../../lib/criteria';
import { getEvidenceUrl } from '../../lib/fileUrls';
import SafeImage from '../../components/common/SafeImage';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { SectionRadarChart } from '../../components/ScoreChart';
import { ArrowLeft, CheckCircle2, AlertTriangle, MessageSquare, RotateCcw, Map, ClipboardList, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { toast } from 'sonner';
import { notifyPublished, notifyReturned } from '../../api/notifications';
import ReportDownload from '../../components/ReportDownload';
import { formatAnswerForPDF, isCriticalFinding } from '../../lib/answerFormat';
import { mapPointsAPI, photoUrl } from '../../api/mapPoints';
import { useJsApiLoader, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';

function ScoreBar({ score, max, percentage }) {
  const pct = percentage ?? 0;
  // #Fase0-B4 — corte único del semáforo (C1), antes ≥81/≥61/≥41.
  const color = percentage == null ? '#94a3b8' : getSemaforoBarColor(pct);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-slate-100 rounded-full h-2">
        <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono font-bold" style={{ color }}>{score}/{max}</span>
    </div>
  );
}

// Score labels for display
const SCORE_LABELS = {
  0: 'NO Cumple',
  2: 'Condiciones por debajo del límite',
  4: 'Condiciones limitadas',
  6: 'Implementado según estándar',
  8: 'Resultados consistentes (6 eventos)',
  10: 'Condición Exportable',
};

// Renders the answer for a non-score-scale question type in read-only
// mode (#198 — used by the Lider review screen). Returns null when the
// question is the legacy score scale so the existing UI keeps the
// score badge / recommendations block.
function ReadOnlyAnswer({ criterion, item }) {
  const qtype = criterion.question_type || 'score_scale';
  if (item?.not_applicable || ['info', 'compliance', 'existence'].includes(qtype))
    return <p className="text-sm text-soem-navy">{formatAnswerForPDF(criterion, item)}</p>;
  if (qtype === 'score_scale') return null;

  if (qtype === 'yes_no') {
    if (item?.answer_bool === null || item?.answer_bool === undefined) {
      return <span className="text-slate-400 italic text-sm">Sin respuesta</span>;
    }
    return (
      <span className="px-3 py-1 rounded-sm border-2 border-soem-primary bg-soem-primary/10 text-soem-primary font-semibold text-sm">
        {item.answer_bool ? 'Sí' : 'No'}
      </span>
    );
  }

  if (qtype === 'dropdown' || qtype === 'single_choice') {
    const selected = (item?.selected_options || [])[0];
    if (!selected) return <span className="text-slate-400 italic text-sm">Sin respuesta</span>;
    const opt = (criterion.options || []).find(o => o.label ===selected);
    return <span className="px-3 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 text-sm">{opt?.label || selected}</span>;
  }

  if (qtype === 'multiple_choice') {
    const selected = item?.selected_options || [];
    if (selected.length === 0) return <span className="text-slate-400 italic text-sm">Sin respuesta</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {selected.map(v => {
          const opt = (criterion.options || []).find(o => o.label ===v);
          return (
            <span key={v} className="px-2 py-0.5 rounded-sm border border-slate-300 bg-white text-slate-700 text-xs">
              {opt?.label || v}
            </span>
          );
        })}
      </div>
    );
  }

  if (qtype === 'numeric') {
    return item?.answer_numeric !== null && item?.answer_numeric !== undefined
      ? <span className="text-soem-navy font-bold">{item.answer_numeric}</span>
      : <span className="text-slate-400 italic text-sm">Sin respuesta</span>;
  }

  if (qtype === 'date') {
    return item?.answer_date
      ? <span className="text-soem-navy font-mono text-sm">{item.answer_date}</span>
      : <span className="text-slate-400 italic text-sm">Sin respuesta</span>;
  }

  if (qtype === 'text_short' || qtype === 'text_long') {
    return item?.answer_text
      ? <p className="text-slate-700 text-sm whitespace-pre-wrap">{item.answer_text}</p>
      : <span className="text-slate-400 italic text-sm">Sin respuesta</span>;
  }

  if (qtype === 'file_upload') {
    return <span className="text-slate-500 text-sm">Ver evidencia abajo</span>;
  }

  return null;
}

function CriterionRow({ item, criterion, onChange, allowNA }) {
  const [expanded, setExpanded] = useState(true);
  const openGallery = useContext(EvidenceGalleryContext);
  const score = item?.score ?? 0;
  const qtype = criterion.question_type || 'score_scale';
  const isScoreScale = qtype === 'score_scale' && !item?.not_applicable;
  // #Fase1-10 — Solo un hallazgo aplicable puede marcarse como bloqueo.
  const isBlocked = isCriticalFinding(criterion, item);
  const finding = isFinding(criterion, item);
  const hasRecommendation = isScoreScale && criterion.recommendations && criterion.recommendations[score];
  
  return (
    <div 
      className={`border-2 rounded-sm transition-all ${
        finding ? 'border-red-400 bg-red-50/50' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`criterion-review-${criterion.id}`}
    >
      <div className="px-4">
        <IntegrityFields
          criterion={criterion}
          item={item}
          allowNA={allowNA}
          onChange={onChange}
          recommendation
          editRecommendation={false}
        />
      </div>
      {/* Header */}
      <div 
        className={`flex items-center justify-between px-4 py-3 cursor-pointer ${isBlocked ? 'bg-red-100 border-b border-red-200' : 'bg-slate-50 border-b border-slate-100'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold px-2 py-1 rounded ${isBlocked ? 'bg-red-600 text-white' : 'bg-soem-navy text-white'}`}>
            #{criterion.id}
          </span>
          <span className="font-bold text-soem-navy text-sm">{criterion.title}</span>
          {criterion.blocker && (
            <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded font-bold flex items-center gap-1">
              <AlertTriangle size={10} /> BLOQUEANTE
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isScoreScale ? (
            <span className={`text-lg font-black px-3 py-1 rounded ${
              score === 10 ? 'bg-blue-100 text-blue-700' :
              score >= 8 ? 'bg-green-100 text-green-700' :
              score >= 4 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {formatAnswerForPDF(criterion, item)}

            </span>
          ) : (
            <span className="text-xs uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-1 rounded font-semibold">
              {(qtype || 'pregunta').replace(/_/g, ' ')}
            </span>
          )}
          {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </div>

      {/* Blocker warning banner */}
      {isBlocked && (
        <div className="bg-red-600 text-white text-sm font-bold px-4 py-2 flex items-center gap-2">
          <AlertTriangle size={14} />
          🚨 EVALUACIÓN BLOQUEADA — Este criterio se publicará con bloqueo
        </div>
      )}

      {/* Content - collapsible */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Focus/Description */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Enfoque del Criterio</p>
            <p className="text-slate-700 text-sm leading-relaxed">{criterion.focus}</p>
          </div>

          {/* Recinto Seguro - shown in blue when score is 10 */}
          {criterion.recintoSeguro && (
            <div className={`p-3 rounded-sm text-sm border-l-4 ${
              score === 10 
                ? 'bg-blue-50 border-blue-500 text-blue-800' 
                : 'bg-slate-50 border-slate-300 text-slate-600'
            }`}>
              <p className="font-semibold text-xs uppercase tracking-wide opacity-70 mb-1">
                {score === 10 ? '✓ Recinto Seguro:' : 'Recinto Seguro:'}
              </p>
              <p className="leading-relaxed">{criterion.recintoSeguro}</p>
            </div>
          )}

          {/* Score label (only for score_scale) */}
          {isScoreScale && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Calificación:</span>
              <span className={`text-sm font-bold ${
                score === 10 ? 'text-blue-700' :
                score >= 8 ? 'text-green-700' :
                score >= 4 ? 'text-yellow-700' :
                'text-red-700'
              }`}>
                {SCORE_LABELS[score] || `Puntaje ${score}`}
              </span>
            </div>
          )}

          {/* Answer display for non-score-scale types (#198) */}
          {!isScoreScale && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Respuesta</p>
              <ReadOnlyAnswer criterion={criterion} item={item} />
            </div>
          )}

          {/* Description of selected level */}
          {isScoreScale && criterion.descriptions?.[score] && (
            <div className="p-3 rounded-sm text-sm bg-slate-50 border border-slate-200">
              <p className="font-semibold text-xs mb-1 uppercase tracking-wide text-slate-500">Descripción del nivel {score}:</p>
              <p className="leading-relaxed text-slate-700">{criterion.descriptions[score]}</p>
            </div>
          )}

          {/* Recommendation based on score */}
          {hasRecommendation && (
            <div className={`p-3 rounded-sm text-sm border-l-4 ${
              score === 10 ? 'bg-blue-50 border-blue-500 text-blue-800' :
              score >= 8 ? 'bg-green-50 border-green-500 text-green-800' :
              score >= 4 ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
              'bg-red-50 border-red-500 text-red-800'
            }`}>
              <p className="font-semibold text-xs mb-1 uppercase tracking-wide opacity-70">Recomendación:</p>
              <p className="leading-relaxed">{criterion.recommendations[score]}</p>
            </div>
          )}

          {/* Notes from levantador */}
          {item?.notes && (
            <div className="bg-slate-50 rounded-sm p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                <MessageSquare size={12} /> Notas del Levantador
              </p>
              <p className="text-slate-700 text-sm">{item.notes}</p>
            </div>
          )}

          {(item?.location || item?.zone) && <p>Ubicación del reactivo: {evidenceStamp(item)}
            {item.location && validPoint(item.location) && <span> · {item.location.lat}, {item.location.lng}</span>}
          </p>}
          {uniqueEvidence(item).length > 0 && <div>
            <p>Evidencia Adjunta</p>
            {uniqueEvidence(item).map((ev, i) => <div key={ev.file_url}>
              {/* Las fotos se abren en la galería de la página; videos y documentos, en otra pestaña. */}
              <a href={getEvidenceUrl(ev.file_url)} target="_blank" rel="noreferrer"
                onClick={(event) => {
                  if (openGallery && isImageEvidence(ev) && shouldOpenGallery(event)) {
                    event.preventDefault();
                    openGallery(ev.file_url);
                  }
                }}>
                {isImageEvidence(ev) &&
                  <SafeImage src={ev.file_url} type="evidence" alt={`Evidencia ${i + 1}`} className="w-20 h-20 cursor-zoom-in" />}
                Ver evidencia {i + 1}
              </a>
              <p className="text-xs">{evidenceStamp(ev)}</p>
            </div>)}
          </div>}
        </div>
      )}
    </div>
  );
}

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const MEXICO_CENTER = { lat: 19.4326, lng: -99.1332 };
const MAP_CONTAINER = { width: '100%', height: '400px' };

const MAP_CFG = {
  hospital:   { color: '#15803D', symbol: 'H' },
  evacuacion: { color: '#1A56DB', symbol: 'E' },
  riesgo:     { color: '#D97706', symbol: 'R' },
  critico:    { color: '#DC2626', symbol: '!' },
};
function getMarkerSvg(category) {
  const c = (MAP_CFG[category]?.color || '#DC2626').replace(/#/g, '%23');
  const s = MAP_CFG[category]?.symbol || '!';
  return `data:image/svg+xml;charset=UTF-8,<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30'><circle cx='15' cy='15' r='13' fill='${c}' stroke='white' stroke-width='2.5'/><text x='15' y='20' text-anchor='middle' fill='white' font-size='13' font-weight='bold' font-family='Arial,sans-serif'>${s}</text></svg>`;
}

export function ReadOnlyMap({ evaluation, mapPoints, loadingPoints }) {
  const { isLoaded, loadError } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: MAPS_KEY });
  const [mapInstance, setMapInstance] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isLoaded || !mapInstance || !evaluation?.general_fields?.ubicacion) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: evaluation.general_fields.ubicacion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        mapInstance.setCenter(results[0].geometry.location);
        mapInstance.setZoom(16);
      }
    });
  }, [isLoaded, mapInstance, evaluation]);

  return (
    <div className="space-y-4">
      {loadError ? <p role="status">Mapa no disponible.</p> : !isLoaded ? <p role="status">Cargando mapa…</p>
        : loadingPoints ? <p role="status">Cargando marcadores…</p> : <div className="soem-card overflow-hidden">
        <GoogleMap mapContainerStyle={MAP_CONTAINER} center={MEXICO_CENTER} zoom={14} onLoad={m => setMapInstance(m)}
          options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
            styles: [
              { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
              { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            ] }}>
          {mapPoints.map(p => (
            <Marker key={p.id} position={{ lat: p.lat, lng: p.lng }}
              icon={getMarkerSvg(p.category)}
              title={MAP_CFG[p.category]?.label || p.category}
              onClick={() => setSelected(selected?.id === p.id ? null : p)} />
          ))}
          {selected && (
            <InfoWindow position={{ lat: selected.lat, lng: selected.lng }} onCloseClick={() => setSelected(null)}>
              <div className="min-w-[150px] max-w-[200px]">
                <p className="font-bold text-sm text-soem-navy mb-1">
                  {MAP_CFG[selected.category]?.label || selected.category}
                </p>
                {selected.notes && <p className="text-xs text-slate-600 mb-2">{selected.notes}</p>}
                {selected.photo_url && (
                  <img src={photoUrl(selected.photo_url)} alt="marker" className="w-full h-24 object-cover rounded" />
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>}
      <EvidenceLayer evaluationId={evaluation.id} map={isLoaded && !loadError && !loadingPoints ? mapInstance : null}
        emptyMessage={mapPoints.length === 0
          ? 'El levantador aún no ha agregado marcadores ni evidencias al mapa.' : ''} />
      {/* Marker list */}
      {mapPoints.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {mapPoints.map(p => (
            <div key={p.id} className="soem-card p-3 flex items-center gap-2.5 cursor-pointer hover:bg-blue-50/40 transition-colors" onClick={() => setSelected(p)}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" style={{ backgroundColor: MAP_CFG[p.category]?.color }}>
                {MAP_CFG[p.category]?.symbol}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold text-soem-navy">{MAP_CFG[p.category]?.label}</p>
                {p.notes && <p className="text-xs text-slate-400 truncate">{p.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EvaluationReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeUser, isAdmin } = useAuth();
  const naSaveChain = useRef(Promise.resolve(true));
  const naLastEvaluation = useRef(null);
  const [missing, setMissing] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [mapPoints, setMapPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [activeTab, setActiveTab] = useState('criterios');
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnModal, setReturnModal] = useState(false);
  const [returnComments, setReturnComments] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [returning, setReturning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const evalRes = await evaluationsAPI.get(id);
        const ev = evalRes.data;
        setEvaluation(ev);
      } catch { toast.error('Error al cargar evaluación'); }
      finally { setLoading(false); }
    }
    load();
  }, [id]);

  // Load map points when tab opens
  useEffect(() => {
    if (activeTab !== 'mapa') return;
    setLoadingPoints(true);
    mapPointsAPI.list(id).then(r => setMapPoints(r.data || [])).catch(() => {}).finally(() => setLoadingPoints(false));
  }, [activeTab, id]);

  // Helper: get levantador info from the evaluation. Antes usábamos
  // evaluation.created_by, pero esa propiedad apunta al usuario que
  // disparó la creación (típicamente un Líder o el Super Admin), no
  // al levantador realmente asignado. El campo correcto es
  // assigned_levantador_id (#WiVhw3i0). Mantenemos created_by como
  // fallback para evals históricas que aún no traen el campo.
  const getLevantadorInfo = async (_unused) => {
    try {
      const levId = evaluation?.assigned_levantador_id || evaluation?.created_by;
      if (!levId) return null;
      const usersData = await usersAPI.list();
      return (usersData.data.items || []).find(u => u.id === levId) || null;
    } catch {
      return null;
    }
  };

  // #Fase2-A7 — El dispositivo puede reportar pendientes después de abrir esta pantalla.
  const refreshBeforeAction = async () => {
    try {
      const { data } = await evaluationsAPI.get(id);
      setEvaluation((current) => ({ ...current, device_pending: data.device_pending }));
      return data;
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo actualizar la evaluación. Intenta de nuevo.'));
      return null;
    }
  };

  const handleSubmitToReview = async () => {
    if ((await naSaveChain.current) === false) {
      toast.error('Guarda los cambios de No aplica antes de publicar');
      return;
    }
    const fresh = await refreshBeforeAction();
    if (!fresh) return;
    if (
      !window.confirm(
        devicePendingMessage(fresh) + '\n' +
          publicationWarning(fresh) + '¿Enviar esta evaluación?'
      )) return;
    setSubmitting(true);
    try {
      await evaluationsAPI.submit(id);
      toast.success('Evaluacion enviada a revision');
      navigate(-1);
    } catch (err) {
      setMissing(missingFromError(err));
      toast.error(errorMessage(err, 'Error al enviar a revision'));
    } finally { setSubmitting(false); }
  };

  const handleForcePublish = async () => {
    if ((await naSaveChain.current) === false) {
      toast.error('Guarda los cambios de No aplica antes de publicar');
      return;
    }
    const fresh = await refreshBeforeAction();
    if (!fresh) return;
    const warnings = [devicePendingMessage(fresh)].filter(Boolean);
    if (fresh.is_blocked) {
      warnings.push(publicationWarning(fresh));
    }
    if (fresh.progress < 100) {
      warnings.push(`Evaluacion incompleta (${fresh.items_scored || '?'}/${fresh.items_total || 35} criterios).`);
    }
    const warningText = warnings.length ? `\n\nAdvertencias:\n${warnings.join('\n')}` : '';
    if (!window.confirm(`¿Publicar directamente esta evaluacion (sin revision)?${warningText}\n\nEsta accion es irreversible.`)) return;
    setPublishing(true);
    try {
      await evaluationsAPI.forcePublish(id);
      toast.success('Evaluacion publicada directamente');
      navigate(-1);
    } catch (err) {
      setMissing(missingFromError(err));
      toast.error(errorMessage(err, 'Error al publicar'));
    } finally { setPublishing(false); }
  };

  const handlePublish = async () => {
    if ((await naSaveChain.current) === false) {
      toast.error('Guarda los cambios de No aplica antes de publicar');
      return;
    }
    const fresh = await refreshBeforeAction();
    if (!fresh) return;
    if (
      !window.confirm(
        devicePendingMessage(fresh) + '\n' + publicationWarning(fresh) +
          '¿Confirmas que deseas aprobar y publicar esta evaluación? Esta acción es irreversible.')) return;
    setPublishing(true);
    try {
      const result = await evaluationsAPI.publish(id);
      const ev = result.data;

      // Notify levantador (best-effort)
      try {
        const levantador = await getLevantadorInfo(evaluation.created_by);
        if (levantador) {
          await notifyPublished({
            levantadorEmail: levantador.email,
            levantadorName: levantador.name,
            senderName: activeUser?.name || 'Líder',
            evaluationName: evaluation.title || evaluation.general_fields?.instalacion || `Evaluación v${evaluation.version}`,
            version: evaluation.version,
            semaforo: ev.semaforo || evaluation.semaforo,
            score: ev.score || evaluation.score,
            maxScore: ev.max_score || evaluation.max_score,
            percentage: evaluationPercentage(ev),
          });
          toast.success('Evaluación publicada — Levantador notificado por email');
        } else {
          toast.success('Evaluación publicada exitosamente');
        }
      } catch {
        toast.success('Evaluación publicada exitosamente');
      }

      navigate(-1);
    } catch (err) {
      setMissing(missingFromError(err));
      toast.error(errorMessage(err, 'Error al publicar'));
    } finally { setPublishing(false); }
  };

  const handleReturn = async () => {
    if (!returnComments.trim()) {
      toast.error('Los comentarios son obligatorios');
      return;
    }
    const fresh = await refreshBeforeAction();
    if (!fresh) return;
    if (devicePendingMessage(fresh) &&
      !window.confirm(`${devicePendingMessage(fresh)}\n¿Regresar de todos modos?`)) return;
    setReturning(true);
    try {
      await evaluationsAPI.returnToLevantador(id, returnComments);

      // Notify levantador (best-effort)
      try {
        const levantador = await getLevantadorInfo(evaluation.created_by);
        if (levantador) {
          await notifyReturned({
            levantadorEmail: levantador.email,
            levantadorName: levantador.name,
            senderName: activeUser?.name || 'Líder',
            evaluationName: evaluation.title || evaluation.general_fields?.instalacion || `Evaluación v${evaluation.version}`,
            version: evaluation.version,
            comments: returnComments,
          });
          toast.success('Evaluación regresada — Levantador notificado por email');
        } else {
          toast.success('Evaluación regresada al levantador');
        }
      } catch {
        toast.success('Evaluación regresada al levantador');
      }

      navigate(-1);
    } catch (err) {
      setMissing(missingFromError(err));
      toast.error(errorMessage(err, 'Error al regresar evaluación'));
    } finally { setReturning(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!evaluation) return <div className="text-center py-12 text-slate-400">Evaluación no encontrada.</div>;

  const items = evaluation.items || [];

  // Template-driven section list (#198). Falls back to legacy CRITERIA
  // sections when no template is attached.
  const isTemplateMode = !!evaluation?.template_id && (evaluation?.sections || []).length > 0;
  const templateSections = isTemplateMode
    ? evaluation.sections.map((s, index) => ({
        name: sectionKey(s, index),
        label: sectionTitle(s),
        criteria: (s.criteria || []).filter((c) => c.visible !== false)
          .map((c) => ({
            ...c,
            id: c.criterion_id ?? c.question_id,
          section: s.section_name || s.section_id,
          title: c.title || c.criterion_text || 'Pregunta',
          focus: c.focus || c.description || '',
          blocker: c.is_blocking || false,
          question_type: c.question_type || 'score_scale',
          options: c.options || [],
          max_score: c.max_score ?? 10,
            // #Fase1-10 — No habilitar la calificación de existence por omisión.
            scoring_enabled: c.scoring_enabled,
        })),
      }))
    : [SECTIONS.EVENTOS, SECTIONS.SECURITY, SECTIONS.SAFETY].map(name => ({
        name,
        criteria: getCriteriaBySection(name).map(c => legacyCriterion(evaluation, c)),
      }));
  const sectionLabels = Object.fromEntries(
    templateSections.map((s) => [s.name, s.label || s.name])
  );
  const sectionsList = templateSections.map(s => s.name);
  const criteriaByName = Object.fromEntries(templateSections.map(s => [s.name, s.criteria]));

  // #Fase1-3 — N/A se confirma en servidor antes de refrescar visibilidad y resumen.
  const sectionStats = getSectionStatsFromEval(evaluation);
  const visibility = visibilityMap(templateSections.flatMap((s) => s.criteria));
  // Galería: todas las fotos de la evaluación, en el orden de los reactivos, para recorrerlas con ← →.
  const galleryPhotos = sectionsList.flatMap((section) => (criteriaByName[section] || [])
    .filter((c) => visibility[c.id] !== false)
    .flatMap((c) => {
      const item = items.find(i => (i.criterio_id ?? i.question_id) === c.id);
      return uniqueEvidence({ ...c, ...item }).filter(isImageEvidence).map((ev, i) => ({
        url: ev.file_url,
        title: `${sectionLabels[section] || section} · ${c.title} — Evidencia ${i + 1}`,
        caption: evidenceStamp(ev),
      }));
    }));
  const openGallery = (url) => {
    const index = galleryPhotos.findIndex((photo) => photo.url === url);
    if (index >= 0) setGalleryIndex(index);
  };
  const updateNA = (criterion, partial) => {
    // #Fase1-3 — Serializar motivo y marca evita invertir dos cambios consecutivos.
    const item = {
      ...criterion,
      ...items.find((entry) => (entry.criterio_id ?? entry.question_id) === criterion.id),
    };
    if (partial.not_applicable && answered(criterion, item)) {
      const response = formatAnswerForPDF(criterion, item);
      const warning =
        `Se borrará la respuesta del evaluador (${response}). ` +
        'Quitar No aplica no la restaura; en revisión habría que regresar la evaluación. ¿Continuar?';
      if (!window.confirm(warning)) return Promise.resolve(false);
    }
    naSaveChain.current = naSaveChain.current.then(async () => {
      let saved;
      try {
        saved = await evaluationsAPI.updateItems(id, [
          { ...itemIdentity(criterion.id, criterion), ...partial },
        ]);
      } catch (err) {
        toast.error(errorMessage(err));
        return false;
      }
      const updated = applyReviewNA(naLastEvaluation.current || evaluation, criterion, partial, saved.data);
      naLastEvaluation.current = updated;
      setEvaluation(updated);
      try {
        const res = await evaluationsAPI.get(id);
        naLastEvaluation.current = res.data;
        setEvaluation(res.data);
      } catch {
        toast.error('No aplica guardado; no se pudo actualizar la vista');
      }
      return true;
    });
    return naSaveChain.current;
  };

  // Build template-question metadata index by criterion_id so the review
  // screen can render dropdown/yes_no/text/etc. answers when the
  // evaluation uses a template (#198).
  const templateMetaByCid = {};
  (evaluation.sections || []).forEach(sec => {
    (sec.criteria || []).forEach(c => {
      const cid = c.criterion_id ?? c.question_id;
      if (cid !== undefined && cid !== null) {
        templateMetaByCid[cid] = c;
      }
      // #Fase0-B2 — blocked_items trae el question_id (UUID) en plantillas;
      // en las migradas criterion_id es el entero legacy y el banner caía
      // al UUID crudo. Se indexa también por question_id.
      if (c.question_id !== undefined && c.question_id !== null) {
        templateMetaByCid[c.question_id] = c;
      }
    });
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="evaluation-review-page">
      {devicePendingMessage(evaluation) && (
        <p role="status" className="bg-amber-50 border border-amber-300 p-3">
          {devicePendingMessage(evaluation)}
        </p>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <button aria-label="Volver" onClick={() => navigate(-1)} className="soem-btn-secondary p-2 mt-1"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-soem-navy">
              {evaluation.general_fields?.instalacion || 'Evaluación'} v{evaluation.version}
            </h1>
            <StatusBadge status={displayStatus(evaluation)} />
          </div>
          <p className="text-slate-500 text-sm mt-1">{evaluation.general_fields?.ubicacion || '—'}</p>
        </div>
      </div>

      {/* Blocked alert */}

      <MissingList missing={missing} />
      <ScoreSummary evaluation={evaluation} />
      {evaluation.is_blocked && (
        <div className="bg-red-50 border border-red-300 rounded-sm p-4 flex items-start gap-3" data-testid="blocked-banner">
          <AlertTriangle size={20} className="text-red-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-800">EVALUACIÓN BLOQUEADA</p>
            {/* Con plantilla, blocked_items trae UUIDs de pregunta; sin
                traducirlos el líder leía una lista de identificadores
                ilegibles. Se muestran los títulos y solo se cae al id
                crudo cuando no se puede resolver. */}
            <p className="text-red-700 text-sm">Los criterios bloqueantes {' '}
              {(evaluation.blocked_items || [])
              .map(id => templateMetaByCid[id]?.title || templateMetaByCid[id]?.criterion_text || id)
                .join(', ')}{' '}
              tienen calificación 0. Al publicar conservará el estado Publicada con bloqueo.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Scores panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="soem-card p-5">
            <p className="soem-label mb-3">Puntaje Global</p>
            <div className="text-center mb-3">
              <SemaforoBadge semaforo={evaluation.semaforo} percentage={evaluationPercentage(evaluation)} size="lg" />
            </div>
            <p className="text-3xl font-black text-soem-navy text-center">
              {evaluation.score_summary ? evaluation.score : '—'}
              /{evaluation.score_summary ? evaluation.max_score : '—'}
            </p>
            <p className="text-slate-400 text-xs text-center mt-1">
              {percentageLabel(evaluationPercentage(evaluation), 1)}
            </p>
            <div className="mt-3">
              <SectionRadarChart sectionStats={sectionStats} size={280} />
            </div>
            <div className="mt-2 space-y-2">
              {Object.entries(sectionStats).map(([sec, s]) => (
                <div key={sec}>
                  <p className="text-xs font-bold text-slate-600 mb-1">{s.title || sec}</p>
                  <ScoreBar score={s.score} max={s.max} percentage={s.percentage} />
                </div>
              ))}
            </div>
          </div>

          {/* Actions for borrador */}
          {evaluation.status === 'borrador' && (
            <div className="soem-card p-5 space-y-3">
              <p className="soem-label">Acciones</p>
              <button onClick={handleSubmitToReview} disabled={submitting}
                className="w-full soem-btn-primary py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-to-review-btn">
                <Send size={15} />
                {submitting ? 'Enviando...' : 'Enviar a Revision'}
              </button>
              {isAdmin && (
                <button onClick={handleForcePublish} disabled={publishing}
                  className="w-full soem-btn-secondary py-2.5 text-sm flex items-center justify-center gap-2 border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                  data-testid="force-publish-btn">
                  <CheckCircle2 size={15} />
                  {publishing ? 'Publicando...' : 'Publicar directamente'}
                </button>
              )}
              {(evaluation.is_blocked || evaluation.progress < 100) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 text-xs text-yellow-800">
                  {evaluation.is_blocked && (
                    <p>{publicationWarning(naLastEvaluation.current || evaluation)}</p>
                  )}
                  {evaluation.progress < 100 && <p>Evaluacion incompleta ({evaluation.items_scored || '?'}/{evaluation.items_total || 35} criterios).</p>}
                </div>
              )}

            </div>
          )}

          {/* Actions for en_revision */}
          {evaluation.status === 'en_revision' && (
            <div className="soem-card p-5 space-y-3">
              <p className="soem-label">Acciones</p>
              <button onClick={handlePublish} disabled={publishing}
                className="w-full soem-btn-primary py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="publish-btn">
                <CheckCircle2 size={15} />
                {publishing ? 'Publicando...' : 'Aprobar y Publicar'}
              </button>
              {evaluation.is_blocked && (
                <p className="text-red-500 text-xs text-center">
                  {publicationWarning(naLastEvaluation.current || evaluation)}
                </p>
              )}
              <button onClick={() => setReturnModal(true)}
                className="w-full soem-btn-secondary py-2.5 text-sm flex items-center justify-center gap-2 border-orange-200 text-orange-700 hover:bg-orange-50"
                data-testid="return-btn">
                <RotateCcw size={15} /> Regresar al Levantador
              </button>

            </div>
          )}

          <ReportDownload evaluation={evaluation} role={activeUser?.role} />

        </div>

        {/* Main area with tabs */}
        <div className="lg:col-span-3 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-slate-200">
            {[
              { id: 'criterios', label: 'Criterios de Evaluación', icon: ClipboardList },
              { id: 'mapa', label: 'Mapa de la Locación', icon: Map },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === tab.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${tab.id}`}>
                <tab.icon size={15} /> {tab.label}
              </button>
            ))}
          </div>

          {/* Map tab */}
          {activeTab === 'mapa' && (
            <ReadOnlyMap evaluation={evaluation} mapPoints={mapPoints} loadingPoints={loadingPoints} />
          )}

          {/* Criteria tab */}
          {activeTab === 'criterios' && (
            <EvidenceGalleryContext.Provider value={openGallery}>
            <div className="space-y-4">
              {sectionsList.map((section) => (
                <div key={section} className="soem-card">
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <h3 className="font-bold text-soem-navy">{sectionLabels[section]}</h3>
                    <span className="text-xs font-mono text-slate-500">
                      {sectionStats[section]?.score || 0}/{sectionStats[section]?.max || 0} pts
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    {(criteriaByName[section] || []).filter((c) => visibility[c.id] !== false)
                      .map((c) => {
                      // Item lookup tolerates both criterio_id (legacy) and
                      // question_id (template-mode UUIDs).
                      const item = items.find(i => (i.criterio_id ?? i.question_id) === c.id);
                      return (
                        <CriterionRow
                          key={c.id}
                            item={{ ...c, ...item, notes: item?.notes ?? c.response_notes }}
                            allowNA={
                              isTemplateMode &&
                              ['borrador', 'regresada', 'en_revision'].includes(evaluation.status)
                            }
                            onChange={
                              ['borrador', 'regresada', 'en_revision'].includes(evaluation.status)
                                ? (partial) => updateNA(c, partial)
                                : undefined
                            }
                          criterion={c}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            </EvidenceGalleryContext.Provider>
          )}
        </div>
      </div>

      <EvidenceLightbox photos={galleryPhotos} index={galleryIndex}
        onIndexChange={setGalleryIndex} onClose={() => setGalleryIndex(null)} />

      {/* Return modal */}
      {returnModal && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-lg animate-fade-in">
            <div className="p-5 border-b border-slate-200">
              <h3 className="font-bold text-soem-navy">Regresar al Levantador</h3>
              <p className="text-slate-500 text-sm mt-1">Escribe los comentarios para el levantador.</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="f-comentarios" className="soem-label block mb-2 flex items-center gap-1"><MessageSquare size={12} /> Comentarios <span className="text-red-500">*</span></label>
                <textarea
id="f-comentarios"                   className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary resize-none"
                  rows={5}
                  placeholder="Describe los cambios necesarios para aprobar la evaluación..."
                  value={returnComments}
                  onChange={(e) => setReturnComments(e.target.value)}
                  data-testid="return-comments-textarea"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReturn}
                  disabled={returning || !returnComments.trim()}
                  className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="confirm-return-btn"
                >
                  <RotateCcw size={14} />
                  {returning ? 'Enviando...' : 'Confirmar y Regresar'}
                </button>
                <button onClick={() => setReturnModal(false)} className="soem-btn-secondary flex-1 py-2.5 text-sm" data-testid="cancel-return-btn">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
